import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { AuthContext } from '../App';
import { 
  ArrowLeft, ArrowRight, Volume2, CheckCircle2, XCircle,
  Zap, Star, Sparkles, RefreshCw, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import CabinCrewLayout from '../layouts/CabinCrewLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Lesson = () => {
  const navigate = useNavigate();
  const { language, level, unitId, topic } = useParams();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [phase, setPhase] = useState('learn'); // learn, quiz, results
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [xpEarned, setXpEarned] = useState(0);

  useEffect(() => {
    if (user && language) {
      loadLessonContent();
    }
  }, [user, language, topic]);

  const loadLessonContent = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/lessons/content/${language}/${topic || 'greetings'}`,
        {},
        { withCredentials: true }
      );
      setContent(response.data);
      setSessionId(response.data.session_id);
    } catch (error) {
      console.error('Error loading lesson:', error);
      toast.error('Ders içeriği yüklenemedi');
      // Navigate back if error
      navigate(`/lessons/${language}`);
    } finally {
      setLoading(false);
    }
  };

  const playAudio = async (text) => {
    try {
      const response = await axios.post(
        `${API}/speaking/text-to-speech`,
        { text },
        { withCredentials: true, responseType: 'blob' }
      );
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audio.play();
    } catch (error) {
      console.error('TTS error:', error);
    }
  };

  const nextCard = () => {
    if (currentIndex < (content?.exercises?.length || 0) - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowAnswer(false);
    } else {
      // Start quiz phase
      startQuiz();
    }
  };

  const prevCard = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowAnswer(false);
    }
  };

  const startQuiz = async () => {
    setPhase('quiz');
    setCurrentIndex(0);
    setAnswers([]);
    
    try {
      const response = await axios.post(
        `${API}/lessons/quiz/${language}`,
        {},
        { withCredentials: true }
      );
      setQuizQuestions(response.data.questions);
      setSessionId(response.data.session_id);
    } catch (error) {
      console.error('Quiz error:', error);
      toast.error('Quiz yüklenemedi');
    }
  };

  const selectAnswer = (answerIndex) => {
    const question = quizQuestions[currentIndex];
    const isCorrect = answerIndex === question.correct_index;
    
    const newAnswers = [...answers, {
      question_index: currentIndex,
      answer: answerIndex,
      is_correct: isCorrect
    }];
    setAnswers(newAnswers);
    
    if (isCorrect) {
      toast.success('Doğru! 🎉');
    } else {
      toast.error('Yanlış!');
    }
    
    // Move to next question after a short delay
    setTimeout(() => {
      if (currentIndex < quizQuestions.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        submitQuiz(newAnswers);
      }
    }, 1000);
  };

  const submitQuiz = async (finalAnswers) => {
    try {
      const response = await axios.post(
        `${API}/lessons/quiz/submit`,
        {
          session_id: sessionId,
          answers: finalAnswers
        },
        { withCredentials: true }
      );
      
      setXpEarned(response.data.miles_earned);
      setPhase('results');
      toast.success(`Tebrikler! ${response.data.miles_earned} Mil kazandın!`);
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Sonuçlar gönderilemedi');
    }
  };

  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
          <div className="text-center">
            <div className="w-20 h-20 border-8 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-xl font-bold text-gray-700">Ders hazırlanıyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }

  // Results Phase
  if (phase === 'results') {
    const correctCount = answers.filter(a => a.is_correct).length;
    const totalQuestions = quizQuestions.length;
    const score = totalQuestions > 0 ? (correctCount / totalQuestions) * 100 : 0;
    
    return (
      <CabinCrewLayout>
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 py-8">
          <div className="container mx-auto px-4 max-w-2xl">
            <Card className="border-4 border-green-400 shadow-2xl">
              <CardContent className="p-8 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Star className="w-12 h-12 text-white" />
                </div>
                
                <h2 className="text-3xl font-black text-gray-800 mb-4">
                  Ders Tamamlandı! 🎉
                </h2>
                
                <div className="grid grid-cols-3 gap-4 mb-8">
                  <div className="bg-green-100 rounded-2xl p-4">
                    <p className="text-3xl font-black text-green-600">{correctCount}/{totalQuestions}</p>
                    <p className="text-sm text-gray-600">Doğru</p>
                  </div>
                  <div className="bg-blue-100 rounded-2xl p-4">
                    <p className="text-3xl font-black text-blue-600">{score.toFixed(0)}%</p>
                    <p className="text-sm text-gray-600">Başarı</p>
                  </div>
                  <div className="bg-yellow-100 rounded-2xl p-4">
                    <p className="text-3xl font-black text-yellow-600">+{xpEarned}</p>
                    <p className="text-sm text-gray-600"> Mil</p>
                  </div>
                </div>
              
              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => {
                    setPhase('learn');
                    setCurrentIndex(0);
                    setAnswers([]);
                  }}
                  variant="outline"
                  className="border-2 border-blue-300"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Tekrar Yap
                </Button>
                <Button
                  onClick={() => navigate(`/lessons/${language}`)}
                  className="bg-gradient-to-r from-green-500 to-blue-500"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Derslere Dön
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      </CabinCrewLayout>
    );
  }

  // Quiz Phase
  if (phase === 'quiz' && quizQuestions.length > 0) {
    const question = quizQuestions[currentIndex];
    const progress = ((currentIndex + 1) / quizQuestions.length) * 100;
    
    // Skip questions without proper data
    const hasValidOptions = question.options && question.options.length > 0;
    const isWordOrder = question.type === 'word_order' && question.words;
    
    // If question is invalid, skip to next
    if (!hasValidOptions && !isWordOrder) {
      // Auto-skip invalid questions
      if (currentIndex < quizQuestions.length - 1) {
        setTimeout(() => {
          setCurrentIndex(currentIndex + 1);
        }, 100);
      } else {
        // Last question, submit what we have
        submitQuiz(answers);
      }
      return (
        <CabinCrewLayout>
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
            <div className="text-center">
              <div className="w-16 h-16 border-6 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto"></div>
              <p className="mt-4 text-lg text-gray-600">Sonraki soru yükleniyor...</p>
            </div>
          </div>
        </CabinCrewLayout>
      );
    }
    
    return (
      <CabinCrewLayout>
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 py-8">
          <div className="container mx-auto px-4 max-w-2xl">
          {/* Progress Header */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold text-gray-600">Quiz</span>
              <span className="font-bold text-purple-600">{currentIndex + 1}/{quizQuestions.length}</span>
            </div>
            <Progress value={progress} className="h-4 bg-purple-200" />
          </div>
          
          {/* Question Card */}
          <Card className="border-4 border-purple-300 shadow-2xl">
            <CardContent className="p-8">
              <Badge className="bg-purple-500 text-white mb-4">
                {question.type?.replace('_', ' ') || 'Quiz'}
              </Badge>
              
              {/* Word Order Question */}
              {isWordOrder ? (
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">
                    Kelimeleri doğru sıraya koy
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    {question.translation}
                  </p>
                  <div className="bg-gray-100 rounded-xl p-4 mb-4">
                    <div className="flex flex-wrap gap-2 justify-center">
                      {question.words?.map((word, idx) => (
                        <span key={idx} className="px-4 py-2 bg-white rounded-lg border-2 border-purple-300 font-bold">
                          {word}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-center text-gray-600 mb-4">Doğru cevap:</p>
                  <div className="flex flex-wrap gap-2 justify-center mb-4">
                    {question.correct_order?.map((word, idx) => (
                      <span key={idx} className="px-4 py-2 bg-green-100 rounded-lg border-2 border-green-400 font-bold text-green-700">
                        {word}
                      </span>
                    ))}
                  </div>
                  <Button
                    onClick={() => {
                      // Mark as correct and move to next
                      const newAnswers = [...answers, {
                        question_index: currentIndex,
                        answer: 0,
                        is_correct: true
                      }];
                      setAnswers(newAnswers);
                      
                      setTimeout(() => {
                        if (currentIndex < quizQuestions.length - 1) {
                          setCurrentIndex(currentIndex + 1);
                        } else {
                          submitQuiz(newAnswers);
                        }
                      }, 500);
                    }}
                    className="w-full bg-gradient-to-r from-green-500 to-blue-500"
                  >
                    Anladım, Devam Et
                  </Button>
                </div>
              ) : (
                /* Standard Options Question */
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-6">
                    {question.question || question.sentence || question.prompt || 'Doğru cevabı seç'}
                  </h3>
                  
                  {/* Audio button for listen_select */}
                  {question.type === 'listen_select' && question.audio_text && (
                    <Button
                      onClick={() => playAudio(question.audio_text)}
                      variant="outline"
                      className="mb-4 w-full border-2 border-purple-300"
                    >
                      <Volume2 className="w-5 h-5 mr-2" />
                      Dinle
                    </Button>
                  )}
                  
                  {/* Options */}
                  <div className="space-y-3">
                    {question.options?.map((option, idx) => (
                      <button
                        key={idx}
                        onClick={() => selectAnswer(idx)}
                        disabled={answers.some(a => a.question_index === currentIndex)}
                        className={`w-full p-4 rounded-xl font-bold text-left transition-all border-3 break-words ${
                          answers.find(a => a.question_index === currentIndex)?.answer === idx
                            ? answers.find(a => a.question_index === currentIndex)?.is_correct
                              ? 'bg-green-100 border-green-500 text-green-700'
                              : 'bg-red-100 border-red-500 text-red-700'
                            : 'bg-white border-gray-200 hover:border-purple-400 hover:bg-purple-50'
                        }`}
                      >
                        <span className="mr-3 text-gray-400">{String.fromCharCode(65 + idx)}</span>
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      </CabinCrewLayout>
    );
  }

  // Learn Phase
  const exercises = content?.exercises || [];
  const currentExercise = exercises[currentIndex];
  const progress = ((currentIndex + 1) / exercises.length) * 100;

  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 py-8">
        <div className="container mx-auto px-4 max-w-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button 
              onClick={() => navigate(`/lessons/${language}`)}
              variant="ghost"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Derslere Dön
            </Button>
          
          <Badge className="bg-gradient-to-r from-green-500 to-blue-500 text-white text-lg px-4 py-2">
            {content?.topic_name || topic}
          </Badge>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="font-bold text-gray-600">İlerleme</span>
            <span className="font-bold text-blue-600">{currentIndex + 1}/{exercises.length}</span>
          </div>
          <Progress value={progress} className="h-4 bg-blue-200" />
        </div>

        {/* Learning Card */}
        {currentExercise && (
          <Card className="border-4 border-blue-300 shadow-2xl mb-6">
            <CardContent className="p-8">
              {/* Word Card */}
              <div className="text-center">
                {/* Word Display */}
                <div 
                  className="mb-6 cursor-pointer group"
                  onClick={() => playAudio(currentExercise.word || currentExercise.audio_text)}
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Volume2 className="w-10 h-10 text-white" />
                  </div>
                  <p className="text-4xl font-black text-gray-800 mb-2">
                    {currentExercise.word}
                  </p>
                  {currentExercise.pronunciation && (
                    <p className="text-lg text-gray-500 italic">
                      /{currentExercise.pronunciation}/
                    </p>
                  )}
                </div>
                
                {/* Translation (hidden by default) */}
                <div className="mb-6">
                  {showAnswer ? (
                    <div className="animate-fade-in">
                      <p className="text-2xl font-bold text-green-600 mb-2">
                        {currentExercise.translation}
                      </p>
                      {currentExercise.example_sentence && (
                        <div className="bg-gray-50 rounded-xl p-4 mt-4">
                          <p className="text-lg text-gray-700 italic mb-1">
                            &quot;{currentExercise.example_sentence}&quot;
                          </p>
                          {currentExercise.example_translation && (
                            <p className="text-sm text-gray-500">
                              {currentExercise.example_translation}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Button
                      onClick={() => setShowAnswer(true)}
                      variant="outline"
                      className="border-2 border-blue-300"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Çeviriyi Göster
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between gap-4">
          <Button
            onClick={prevCard}
            disabled={currentIndex === 0}
            variant="outline"
            className="flex-1 border-2 border-gray-300"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Önceki
          </Button>
          
          <Button
            onClick={nextCard}
            className="flex-1 bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600"
          >
            {currentIndex === exercises.length - 1 ? (
              <>
                Quiz&apos;e Geç
                <Zap className="w-5 h-5 ml-2" />
              </>
            ) : (
              <>
                Sonraki
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
    </CabinCrewLayout>
  );
};

export default Lesson;
